<template>
  <div data-test="bulk-job-live" class="tw-sr-only" role="status" aria-live="polite" aria-atomic="true">
    {{ liveText }}
  </div>

  <div
    v-if="jobs.length"
    data-test="bulk-job-dock"
    class="bulk-job-dock tw-bg-white dark:tw-bg-neutral-low tw-rounded-lg tw-shadow-lg tw-text-sm"
  >
    <div class="tw-flex tw-items-center tw-justify-between tw-gap-2 tw-px-3 tw-py-2">
      <span data-test="bulk-job-dock-title" class="tw-font-bold">{{ title }}</span>
      <div class="tw-flex tw-items-center">
        <q-btn
          flat
          dense
          round
          size="sm"
          :icon="collapsed ? 'expand_less' : 'expand_more'"
          :aria-label="collapsed ? t('starlink.bulkJobsExpand') : t('starlink.bulkJobsCollapse')"
          data-test="bulk-job-dock-collapse"
          @click="collapsed = !collapsed"
        />
        <q-btn
          flat
          dense
          round
          size="sm"
          icon="close"
          :aria-label="t('starlink.bulkJobsClearFinished')"
          data-test="bulk-job-dock-close"
          @click="clearFinished"
        />
      </div>
    </div>

    <q-separator v-if="!collapsed" />

    <div v-if="!collapsed" class="tw-flex tw-flex-col">
      <div
        v-for="job in jobs"
        :key="job.id"
        data-test="bulk-job-row"
        class="tw-flex tw-flex-col tw-gap-1 tw-px-3 tw-py-2"
      >
        <div class="tw-flex tw-items-start tw-justify-between tw-gap-2">
          <button
            v-if="job.returnTo"
            type="button"
            class="bulk-job-dock__open"
            data-test="bulk-job-open"
            :title="t('starlink.bulkJobsOpenPage')"
            @click="openJob(job)"
          >
            {{ job.label }}
          </button>
          <span v-else>{{ job.label }}</span>
          <q-btn
            v-if="job.status !== 'running'"
            flat
            dense
            round
            size="xs"
            icon="close"
            :aria-label="t('starlink.bulkJobsDismiss')"
            data-test="bulk-job-dismiss"
            @click="dismissJob(job.id)"
          />
        </div>

        <template v-if="job.status === 'running'">
          <q-linear-progress :value="job.total ? job.done / job.total : 0" rounded size="6px" color="primary" />
          <span class="tw-opacity-60">
            {{ t("starlink.bulkJobsProgress", { done: job.done, total: job.total }) }}
          </span>
        </template>

        <div v-else class="tw-flex tw-items-start tw-justify-between tw-gap-3">
          <span data-test="bulk-job-outcome" :data-tone="toneFor(job)" :class="TONE_CLASS[toneFor(job)]">
            {{ job.status === "failed" ? job.error : outcomeText(job) }}
          </span>
          <button
            v-if="canView(job)"
            type="button"
            class="bulk-job-dock__open tw-shrink-0 tw-text-primary-medium-theme-color"
            data-test="bulk-job-view"
            @click="requestView(job.id)"
          >
            {{ t("starlink.bulkJobsView") }}
          </button>
        </div>
      </div>

      <p
        v-if="runningCount"
        data-test="bulk-job-keep-open"
        class="tw-m-0 tw-px-3 tw-pb-2 tw-pt-1 tw-text-xs tw-opacity-60"
      >
        {{ t("starlink.bulkJobsKeepOpen") }}
      </p>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import { useRoute, useRouter } from "vue-router";
import { useBulkJobs, type BulkJob } from "@/composables/useBulkJobs";

const { t } = useI18n();
const router = useRouter();
const route = useRoute();
const { jobs, runningCount, requestView, dismissJob, clearFinished } = useBulkJobs();

const collapsed = ref(false);

// only the page the run started on can draw the breakdown, so off that page the link would have
// nothing to open. they get the way back instead.
const canView = (job: BulkJob) =>
  Boolean(job.view) && job.status !== "running" && (!job.returnTo || route.path === job.returnTo.split("?")[0]);

// the dock is the only thing on screen once they have wandered off, so it carries the way back
const openJob = (job: BulkJob) => {
  if (job.returnTo) {
    router.push(job.returnTo);
  }
};

type Tone = "success" | "warning" | "danger";

// same red and amber the alerts page uses, and the green OneWebApiStatus uses
const TONE_CLASS: Record<Tone, string> = {
  success: "tw-text-success dark:tw-text-success-medium-dark",
  warning: "tw-text-amber-600 dark:tw-text-amber-400",
  danger: "tw-text-red-500 dark:tw-text-red-400",
};

// a run that wrote nothing reads the same whether every target was a duplicate, ibis refused them
// all, or the job threw before it got anywhere.
const toneFor = (job: BulkJob): Tone => {
  if (job.status === "failed" || !job.outcome?.created) {
    return "danger";
  }
  return job.outcome.skipped || job.outcome.failed ? "warning" : "success";
};

// nothing went wrong, so don't make them read two zeroes to find that out. the caller picks the
// verb because outcome.created carries the updated count on an update run.
const outcomeText = (job: BulkJob) => {
  if (!job.outcome) {
    return job.label;
  }
  return toneFor(job) === "success" ? t(job.successKey, { count: job.outcome.created }) : t(job.countsKey, job.outcome);
};

const title = computed(() =>
  runningCount.value ? t("starlink.bulkJobsRunning", { count: runningCount.value }) : t("starlink.bulkJobsTitle")
);

// the bar has aria-valuenow but nothing announces it once focus has moved on, and the whole point
// of the dock is that the user walked away. same wording as the rows so both read the same.
const liveText = computed(() =>
  jobs.value
    .map((job) => {
      if (job.status === "running") {
        return `${job.label}, ${t("starlink.bulkJobsProgress", { done: job.done, total: job.total })}`;
      }
      if (job.status === "failed") {
        return `${job.label}, ${job.error}`;
      }
      return job.outcome ? `${job.label}, ${outcomeText(job)}` : job.label;
    })
    .join(". ")
);

// nothing on the server knows a batch is half done, so a reload loses the run and leaves the
// monitors it already wrote. browsers ignore custom text here, the prompt is generic.
const warnOnLeave = (event: BeforeUnloadEvent) => {
  if (runningCount.value) {
    event.preventDefault();
    event.returnValue = "";
  }
};

// an always on beforeunload costs firefox its back forward cache, so it goes on with the first
// run and comes off with the last one.
watch(
  runningCount,
  (running, wasRunning) => {
    if (running && !wasRunning) {
      window.addEventListener("beforeunload", warnOnLeave);
    } else if (!running && wasRunning) {
      window.removeEventListener("beforeunload", warnOnLeave);
    }
  },
  { immediate: true }
);

onBeforeUnmount(() => window.removeEventListener("beforeunload", warnOnLeave));
</script>

<style scoped lang="scss">
.bulk-job-dock__open {
  padding: 0;
  border: 0;
  background: none;
  color: inherit;
  font: inherit;
  text-align: left;
  text-decoration: underline;
  text-underline-offset: 2px;
  cursor: pointer;
}

.bulk-job-dock {
  position: fixed;
  right: 16px;
  bottom: 16px;
  z-index: 7000;
  width: 320px;
  max-width: calc(100vw - 32px);
}
</style>
